import { Automation } from 'app/automations/automations-models';
import {
  ButtonComponent,
  WidgetComponent,
  WidgetComponentSubType,
  WidgetState,
  Widget,
} from 'app/campaign/widgets.model';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { LanguageService } from 'app/core/language.service';
import { SubmitButtonLocation } from './submit-button-location';

/**
 * Searches top-level components and one level of children. Returns the live component reference so callers
 * can mutate `actionType`/`actionValue` in place.
 */
export function findSubmitButtonLocation(widget: Widget | null | undefined): SubmitButtonLocation | null {
  for (const state of widget?.states ?? []) {
    for (const container of state.containers ?? []) {
      for (const component of container.components ?? []) {
        if (!component) {
          continue;
        }
        if (WidgetComponentHelpers.isSubmitType(component)) {
          (component as ButtonComponent).containerId = container.id;
          return { button: component as ButtonComponent, stateId: state.id, containerId: container.id };
        }
        const childButton = (component.children ?? []).find((child: WidgetComponent) =>
          WidgetComponentHelpers.isSubmitType(child),
        );
        if (childButton) {
          (childButton as ButtonComponent).containerId = container.id;
          return { button: childButton as ButtonComponent, stateId: state.id, containerId: container.id };
        }
      }
    }
  }
  return null;
}

export function widgetHasSubscriptionCheckbox(widget: Widget | null | undefined): boolean {
  return (widget?.states ?? []).some((state) =>
    (state.containers ?? []).some((container) =>
      (container.components ?? []).some(
        (component) =>
          component?.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox ||
          (component?.children ?? []).some(
            (child) => child?.subType === WidgetComponentSubType.ButtonListSubscriptionCheckbox,
          ),
      ),
    ),
  );
}

export function buildChoosableStates(
  widget: Widget | null | undefined,
  submitStateId: number | null | undefined,
  languageService: LanguageService,
  ...excludeStateIds: Array<number | null | undefined>
): WidgetState[] {
  const excluded = new Set([submitStateId, ...excludeStateIds].filter((id): id is number => id != null));
  return [...(widget?.states ?? [])]
    .sort((a, b) => a.order - b.order)
    .filter((state) => !excluded.has(state.id))
    .map((state) => ({ ...state, titleState: WidgetComponentHelpers.pickTitleForState(state, languageService) }));
}

export interface NewSubscriberState {
  /** true = runs only for new subscribers, false = runs for everyone, null = not editable here. */
  value: boolean | null;
  /** Disabled when the trigger is managed in the automation editor (no Tags trigger, or a non-NewSubscriber one). */
  disabled: boolean;
}

export function deriveNewSubscriberState(automation: Automation | null | undefined): NewSubscriberState {
  if (!automation?.triggers) {
    return { value: null, disabled: true };
  }
  const tagsTrigger = automation.triggers.find((trigger) => trigger.type === 'Tags');
  if (!tagsTrigger) {
    return { value: false, disabled: false };
  }
  if (tagsTrigger.operator === 'Equals' && tagsTrigger.argument === 'NewSubscriber') {
    return { value: true, disabled: false };
  }
  return { value: null, disabled: true };
}

/** Apps that don't expose selectable lists. */
const APPS_WITHOUT_LISTS: ReadonlyArray<string> = ['Centra', 'Shopify', 'Omnisend'];

export function integrationHasLists(appName: string | null | undefined): boolean {
  return !!appName && !APPS_WITHOUT_LISTS.includes(appName);
}
